import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  EmailSearchResult,
  EmailSearchResultContact,
  EmailSearchResultProject,
} from '../../../models/Message';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import { AddressesContext } from '../context/AddressesContext';
import { ProjectId } from '../../../models/Types';
import { apiFetchAddressSuggestions } from '../api';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  fetchExternalProjectContacts,
  fetchInternalProjectContacts,
} from '../../projects/actions';

export const sortAddresses: (
  a: EmailSearchResultContact | EmailSearchResultProject,
  b: EmailSearchResultContact | EmailSearchResultProject
) => number = (a, b) => {
  if (a.eMail === b.eMail) {
    if (a.type === 'contact') {
      const castA: EmailSearchResultContact = a as EmailSearchResultContact;
      const castB: EmailSearchResultContact = b as EmailSearchResultContact;
      const lastNameCompare = castA.lastName?.localeCompare(castB.lastName);
      if (lastNameCompare !== 0) return lastNameCompare;
      return castA.firstName?.localeCompare(castB.firstName);
    } else {
      const castA: EmailSearchResultProject = a as EmailSearchResultProject;
      const castB: EmailSearchResultProject = b as EmailSearchResultProject;
      const lastNameCompare = castA.projectNumber?.localeCompare(
        castB.projectNumber
      );
      if (lastNameCompare !== 0) return lastNameCompare;
      return castA.projectName?.localeCompare(castB.projectName);
    }
  }
  return a?.eMail?.localeCompare(b.eMail);
};

interface AddressesContextProviderProps {
  projectId: ProjectId;
  children: ReactNode;
}

const AddressesContextProvider = (props: AddressesContextProviderProps) => {
  //#region ------------------------------ Defaults
  const { children, projectId } = props;
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { contacts } = useContactsContext();
  const { myProjects, getProjectById } = useProjectsContext();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['addressSuggestions', projectId],
    queryFn: async () => await apiFetchAddressSuggestions(projectId, 20),
    enabled: projectId !== 'favorites',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const addressSuggestions: EmailSearchResult[] = useMemo(() => {
    const suggestions = data?.data ?? [];
    return suggestions
      .map((suggestion, index) => {
        if (suggestion.type === 'external') {
          return {
            id: `${index}`,
            eMail: suggestion.eMail,
            type: suggestion.type,
            isSuggestion: true,
          };
        }
        return {
          ...suggestion,
          isSuggestion: true,
        };
      })
      .filter(
        (suggestion) =>
          suggestion.type !== 'project' ||
          !getProjectById(suggestion.id)?.parentProject
      );
  }, [data, getProjectById]);

  const addressesByMail: Record<string, EmailSearchResult> = useMemo(() => {
    const offlineContacts: EmailSearchResult[] = contacts.map((contact) => ({
      id: contact.contactId,
      eMail: contact.eMail ?? contact.eMail2 ?? null,
      type: 'contact',
      lastName: contact.lastName,
      firstName: contact.firstName,
    }));

    const offlineProjects: EmailSearchResult[] = myProjects.map((project) => {
      const parentProject = getProjectById(project.parentProject);
      if (
        !project.parentProject ||
        (parentProject && parentProject?.eMailPrefix !== project?.eMailPrefix)
      ) {
        return {
          id: project.projectId,
          eMail: `${project.eMailPrefix}@${project.eMailSuffix}`,
          type: 'project',
          projectName: project.name,
          projectNumber: project.number,
        };
      }
      return null;
    });

    let result: Record<string, EmailSearchResult> = {};
    offlineContacts.forEach((contact) => {
      if (contact?.eMail) {
        result[contact.eMail] = contact;
      }
    });
    offlineProjects.forEach((project) => {
      if (project?.eMail) {
        result[project.eMail] = project;
      }
    });
    addressSuggestions.forEach((suggestion) => {
      if (suggestion?.eMail) {
        result[suggestion.eMail] = suggestion;
      }
    });
    return result;
  }, [contacts, myProjects, addressSuggestions, getProjectById]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const refetchAddressSugestions = useCallback(() => {
    if (projectId !== 'favorites') {
      refetch();
    }
  }, [projectId, refetch]);

  const getAddressByMail = useCallback(
    (mail: string): EmailSearchResult | undefined => {
      return addressesByMail[mail];
    },
    [addressesByMail]
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (projectId !== 'me' && projectId !== 'favorites') {
      dispatch(fetchInternalProjectContacts(projectId));
      dispatch(fetchExternalProjectContacts(projectId));
    }
  }, [dispatch, projectId]);
  //#endregion

  return (
    <AddressesContext.Provider
      value={{
        addresses: [],
        addressSuggestions,
        isLoading,
        refetchAddressSugestions,
        getAddressByMail,
      }}
    >
      {children}
    </AddressesContext.Provider>
  );
};

export default AddressesContextProvider;
